import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';

import SiteAnalysisSummary from '../components/SiteAnalysisSummary';
import ScreenshotThumbnail from '../components/ScreenshotThumbnail';
import ScoreHistoryGraph from '../components/ScoreHistoryGraph';
import ScoreRowsTable from '../components/ScoreRowsTable';
import PdfExportModal from '../components/PdfExportModal';
import SiteModal from './modals/SiteModal.js';
import { is_site_dashboard_pending, is_site_pre_processing, is_site_history_running, is_site_crawl_running, is_competitor_site, get_pre_processing_label, get_dashboard_pending_text } from '../components/SitesTable';
import { fetch_analysis_running } from '../utils/AnalysisRunning';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import { export_site_pdf, stop_site_pdf_polling } from '../utils/SitePdfExport';

const c_NoRunsText = 'No site analysis has finished yet. Run one from the site page.';

// The image types a screenshot can be uploaded as, as the backend accepts them.
const c_ScreenshotUploadTypes = 'image/jpeg,image/png';

const c_UploadScreenshotWhileCrawlingText = 'A screenshot can be uploaded once the site\'s pages have been processed.';

// How often the competitor table is reloaded while any of its sites is still being crawled or analyzed.
const c_CompetitorPollIntervalMs = 5000;

// How often the site and its runs are reloaded while its history is imported or analyzed.
const c_HistoryPollIntervalMs = 5000;

// One site's dashboard: where the site stands now, how its scores have moved across runs, and the runs themselves.
// Three stacked sections: the header strip, the graph, the run list. A main site's dashboard ends with its
// competitor table and can add a competitor; a competitor's says which site it is a competitor of.
function SiteDashboardView(){
	const navigate = useNavigate();
	const { token } = useAuth();
	const { id } = useParams();

	const [site, set_site] = useState(null);
	const [runs, set_runs] = useState(null);
	const [error_message, set_error_message] = useState('');

	// A main site's competitor table rows, one per competitor, and how many competitors a site can have.
	const [competitor_rows, set_competitor_rows] = useState(null);
	const [max_competitor_sites, set_max_competitor_sites] = useState(0);
	const [show_competitor_modal, set_show_competitor_modal] = useState(false);
	// Whether the user has an analysis going anywhere, page or site: they run one at a time, so Analyze is disabled.
	const [analysis_running, set_analysis_running] = useState(false);

	// The screenshot upload: its hidden file input, and whether an upload is under way.
	const screenshot_input_ref = useRef(null);
	const [uploading_screenshot, set_uploading_screenshot] = useState(false);

	const mounted_ref = useRef(true);
	const competitor_poll_timeout_ref = useRef(null);
	const history_poll_timeout_ref = useRef(null);

	// The site report export of the latest analysis: the PDF Export button on the summary strip, its modal and its
	// error, and the export's progress poll (see SitePdfExport).
	const [fetching_pdf, set_fetching_pdf] = useState(false);
	const [pdf_stage, set_pdf_stage] = useState('');
	const [pdf_error_message, set_pdf_error_message] = useState('');
	const pdf_poll_interval_ref = useRef(null);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	useEffect(()=>{
		return ()=>{
			stop_site_pdf_polling(pdf_poll_interval_ref);
		};
	}, []);

	useEffect(()=>{
		mounted_ref.current = true;
		load_dashboard();

		return ()=>{
			mounted_ref.current = false;
			stop_competitor_polling();
			stop_history_polling();
		};
	}, [id]);

	const stop_history_polling = ()=>{
		if(history_poll_timeout_ref.current){
			clearTimeout(history_poll_timeout_ref.current);
			history_poll_timeout_ref.current = null;
		}
	};

	const schedule_history_reload = ()=>{
		stop_history_polling();
		if(!mounted_ref.current){
			return;
		}

		history_poll_timeout_ref.current = setTimeout(()=>{
			history_poll_timeout_ref.current = null;
			load_history_progress();
		}, c_HistoryPollIntervalMs);
	};

	const load_runs = async()=>{
		const runs_response = await axios.get(Constants.SERVER_URL + '/api/site_analyses/' + id, { headers: get_headers() });
		if(mounted_ref.current){
			set_runs((runs_response.data && runs_response.data.site_analyses) ? runs_response.data.site_analyses : []);
		}
	};

	// Reloads the site and its runs while its history is imported or analyzed, so the progress and each historical
	// run show without a refresh. A failed load is tried again later.
	const load_history_progress = async()=>{
		try {
			const site_response = await axios.get(Constants.SERVER_URL + '/api/site/' + id, { headers: get_headers() });
			if(!mounted_ref.current || !site_response.data || !site_response.data.site){
				return;
			}

			set_site(site_response.data.site);
			await load_runs();

			if(is_site_history_running(site_response.data.site)){
				schedule_history_reload();
			}
		}
		catch (error) {
			console.log('SiteDashboardView/load_history_progress - ERROR: ', describe_error(error));
			schedule_history_reload();
		}
	};

	const stop_competitor_polling = ()=>{
		if(competitor_poll_timeout_ref.current){
			clearTimeout(competitor_poll_timeout_ref.current);
			competitor_poll_timeout_ref.current = null;
		}
	};

	// Reloads the competitor table again later while any of its sites is still being crawled or analyzed, so a
	// competitor's scores appear without a refresh.
	const schedule_competitor_reload = (rows)=>{
		stop_competitor_polling();

		const pre_processing = rows.some((row)=>{ return is_site_pre_processing(row); });
		if(!pre_processing || !mounted_ref.current){
			return;
		}

		competitor_poll_timeout_ref.current = setTimeout(()=>{
			competitor_poll_timeout_ref.current = null;
			load_competitors();
		}, c_CompetitorPollIntervalMs);
	};

	const load_competitors = async()=>{
		try {
			const competitors_response = await axios.get(Constants.SERVER_URL + '/api/site_competitors/' + id, { headers: get_headers() });
			const rows = (competitors_response.data && competitors_response.data.rows) ? competitors_response.data.rows : [];
			if(!mounted_ref.current){
				return;
			}

			set_error_message('');
			set_competitor_rows(rows);
			schedule_competitor_reload(rows);
		}
		catch (error) {
			console.log('SiteDashboardView/load_competitors - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'The competitors could not be loaded.'));
				schedule_competitor_retry();
			}
		}
	};

	// After a failed load the competitor table is loaded again later, so it recovers once the server answers.
	const schedule_competitor_retry = ()=>{
		stop_competitor_polling();
		competitor_poll_timeout_ref.current = setTimeout(()=>{
			competitor_poll_timeout_ref.current = null;
			load_competitors();
		}, c_CompetitorPollIntervalMs);
	};

	const load_site_limits = async()=>{
		try {
			const limits_response = await axios.get(Constants.SERVER_URL + '/api/site_limits', { headers: get_headers() });
			if(mounted_ref.current && limits_response.data){
				set_max_competitor_sites(limits_response.data.max_competitor_sites);
			}
		}
		catch (error) {
			console.log('SiteDashboardView/load_site_limits - ERROR: ', describe_error(error));
		}
	};

	const load_analysis_running = async()=>{
		const running = await fetch_analysis_running(token);
		if(mounted_ref.current){
			set_analysis_running(running);
		}
	};

	const load_dashboard = async()=>{
		load_analysis_running();

		try {
			set_error_message('');
			set_competitor_rows(null);
			stop_competitor_polling();
			stop_history_polling();

			const site_response = await axios.get(Constants.SERVER_URL + '/api/site/' + id, { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			if(!site_response.data || !site_response.data.site){
				set_error_message('Site not found.');
				return;
			}

			set_site(site_response.data.site);

			// A site whose history is still being imported or analyzed has no dashboard yet, so its runs are not loaded.
			if(is_site_dashboard_pending(site_response.data.site)){
				return;
			}

			await load_runs();

			if(is_site_history_running(site_response.data.site)){
				schedule_history_reload();
			}

			if(!is_competitor_site(site_response.data.site)){
				await load_site_limits();
				await load_competitors();
			}
		}
		catch (error) {
			console.log('SiteDashboardView/load_dashboard - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'The site dashboard could not be loaded.'));
				set_runs([]);
			}
		}
	};

	const get_latest_run = ()=>{
		return (runs && (runs.length > 0)) ? runs[0] : null;
	};

	const on_pdf_export = (event)=>{
		event.preventDefault();
		const latest_run = get_latest_run();
		if(latest_run){
			export_site_pdf(token, latest_run.id, {
				set_fetching_pdf,
				set_pdf_stage,
				set_error_message: set_pdf_error_message,
				poll_interval_ref: pdf_poll_interval_ref
			});
		}
	};

	const open_run = (row)=>{
		navigate('/site/' + id + '/analysis/' + row.id);
	};

	// Opens the site page and starts a site analysis there (see SiteView's start_analysis).
	const open_site = ()=>{
		navigate('/site/' + id, { state: { start_analysis: true } });
	};

	// A row of the competitor table opens that site's dashboard.
	// A competitor's dashboard opens only once its import has finished (see is_site_dashboard_pending).
	const open_competitor = (row)=>{
		const competitor = competitor_rows.find((competitor_row)=>{
			return competitor_row.id === row.id;
		});

		if(!competitor || is_site_dashboard_pending(competitor)){
			return;
		}

		navigate('/site/' + row.id + '/dashboard');
	};

	const open_competitor_modal = ()=>{
		set_show_competitor_modal(true);
	};

	const close_competitor_modal = ()=>{
		set_show_competitor_modal(false);
	};

	// Called by SiteModal. Throws an Error carrying the message to show, so the modal stays open on a failure.
	const save_competitor = async(site_data)=>{
		try {
			await axios.post(Constants.SERVER_URL + '/api/sites/', site_data, { headers: get_headers() });
		}
		catch (error) {
			console.log('SiteDashboardView/save_competitor - ERROR: ', describe_error(error));
			throw new Error(get_user_error_message(error, 'The competitor could not be added. Please try again.'));
		}

		close_competitor_modal();
		await load_competitors();
	};

	// A competitor can be added to a main site with fewer than the most competitors a site can have.
	const can_add_competitor = ()=>{
		if(!competitor_rows || is_competitor_site(site)){
			return false;
		}

		return competitor_rows.length < max_competitor_sites;
	};

	const format_run_date = (run_date)=>{
		const date = new Date(run_date);
		return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
			' ' + date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
	};

	// A url as shown: no scheme, no leading www., no trailing slash. Also how a site's name is compared with its url.
	const strip_url = (url)=>{
		return String(url).replace('https://', '').replace('http://', '').replace(/^www\./i, '').replace(/\/+$/, '');
	};

	// The link to the site itself, shown as its bare host.
	const render_site_link = (class_name)=>{
		return (
			<a className={class_name} href={site.url} target="_blank" rel="noopener noreferrer">
				{strip_url(site.url)}
			</a>
		);
	};

	// The site's name with the link to the site under it, or the link alone as the title when the name is the url.
	const render_site_name = ()=>{
		const name_is_url = !site.title || (strip_url(site.title).toLowerCase() === strip_url(site.url).toLowerCase());

		if(name_is_url){
			return render_site_link('site-title site-title-link');
		}

		return (
			<React.Fragment>
				<div className="site-title">{site.title}</div>
				{render_site_link('site-dashboard-url')}
			</React.Fragment>
		);
	};

	// The header strip: title, url, last run date and coverage, with the latest run's thumbnail, then the latest
	// run's grade and scores in the same strip the site analysis view uses.
	const render_header = ()=>{
		if(!site){
			return null;
		}

		const latest_run = get_latest_run();

		return (
			<React.Fragment>
				<Row className="site-title-row">
					<Col>
						<div className="site-title-block">
							<div>
								{render_site_name()}
								{render_competitor_of()}
								{render_run_facts(latest_run)}
								{render_history_status()}
							</div>
							{render_thumbnail(latest_run)}
						</div>
					</Col>
					<Col className="site-title-actions">
						<Button variant="primary" className="analyze-site-button" disabled={analysis_running} onClick={open_site}>
							Analyze
						</Button>
						{render_add_competitor_button()}
					</Col>
				</Row>

				{latest_run ? <SiteAnalysisSummary site_analysis={latest_run} on_pdf_export={on_pdf_export} fetching_pdf={fetching_pdf}/> : null}
			</React.Fragment>
		);
	};

	// A competitor's dashboard says which of the user's sites it is a competitor of, with a link back to that site's
	// dashboard.
	const render_competitor_of = ()=>{
		if(!is_competitor_site(site) || !site.competitor_of){
			return null;
		}

		return (
			<div className="site-competitor-of">
				{'Competitor of '}
				<Link className="site-competitor-of-link" to={'/site/' + site.competitor_of.id + '/dashboard'}>
					{site.competitor_of.title}
				</Link>
			</div>
		);
	};

	const render_add_competitor_button = ()=>{
		if(!can_add_competitor()){
			return null;
		}

		return (
			<Button variant="primary" className="analyze-site-button add-competitor-button" onClick={open_competitor_modal}>
				Add Competitor
			</Button>
		);
	};

	const render_run_facts = (latest_run)=>{
		if(!latest_run){
			return null;
		}

		const total_pages = site.num_site_pages ? site.num_site_pages : 0;
		return (
			<React.Fragment>
				<div className="site-coverage">{'Latest data ' + format_run_date(latest_run.page_data_date)}</div>
				<div className="site-coverage">{latest_run.num_pages + ' of ' + total_pages + ' pages analyzed'}</div>
			</React.Fragment>
		);
	};

	// While the site's history is imported or analyzed, its progress, labelled as on the sites list.
	const render_history_status = ()=>{
		if(!is_site_history_running(site)){
			return null;
		}

		return (
			<div className="site-coverage">
				<span className="sites-table-pre-processing">
					<Spinner animation="border" size="sm" className="sites-table-pre-processing-spinner"/>
					{get_pre_processing_label(site)}
				</span>
			</div>
		);
	};

	const open_screenshot_picker = ()=>{
		if(screenshot_input_ref.current){
			screenshot_input_ref.current.click();
		}
	};

	// Sends the chosen image as the site's screenshot, its type as the request's Content-Type, and shows the site the
	// backend returns, with the new thumbnail.
	const upload_screenshot = async(event)=>{
		const file = (event.target.files && (event.target.files.length > 0)) ? event.target.files[0] : null;
		event.target.value = '';
		if(!file){
			return;
		}

		set_uploading_screenshot(true);
		try {
			const response = await axios.post(Constants.SERVER_URL + '/api/site_screenshot/' + id, file, {
				headers: Object.assign(get_headers(), { 'Content-Type': file.type })
			});
			if(mounted_ref.current && response.data && response.data.site){
				set_error_message('');
				set_site(Object.assign({}, site, response.data.site));
			}
		}
		catch (error) {
			console.log('SiteDashboardView/upload_screenshot - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_error_message(get_user_error_message(error, 'The screenshot could not be uploaded. Please try again.'));
			}
		}
		finally {
			if(mounted_ref.current){
				set_uploading_screenshot(false);
			}
		}
	};

	// The Upload Screenshot button and its hidden file input. Disabled while the site's pages are processed, as the
	// backend refuses an upload then.
	const render_screenshot_upload = ()=>{
		const crawl_running = is_site_crawl_running(site);
		return (
			<React.Fragment>
				<input
					ref={screenshot_input_ref}
					type="file"
					accept={c_ScreenshotUploadTypes}
					className="site-screenshot-input"
					onChange={upload_screenshot}/>
				<Button
					variant="link"
					size="sm"
					className="site-screenshot-upload-button"
					title={crawl_running ? c_UploadScreenshotWhileCrawlingText : ''}
					disabled={uploading_screenshot || crawl_running}
					onClick={open_screenshot_picker}>
					{uploading_screenshot ? 'Uploading...' : 'Replace image'}
				</Button>
			</React.Fragment>
		);
	};

	// The site's own thumbnail, taken from the live homepage when the site was crawled or uploaded by the user, else
	// the latest analysis's, with the button to upload a replacement under it.
	const render_thumbnail = (latest_run)=>{
		let image_id = site.screenshot_thumbnail_image_id ? site.screenshot_thumbnail_image_id : null;
		if(!image_id && latest_run && latest_run.screenshot_thumbnail_image_id){
			image_id = latest_run.screenshot_thumbnail_image_id;
		}

		return (
			<div className="site-title-screenshot">
				{image_id ? <ScreenshotThumbnail image_id={image_id} alt_text="Screenshot of the homepage"/> : null}
				{render_screenshot_upload()}
			</div>
		);
	};

	const render_graph = ()=>{
		if(!runs || (runs.length === 0)){
			return null;
		}

		return (
			<Card className="site-dashboard-graph-card">
				<h5 className="site-dashboard-section-title">Scores Across Analyses</h5>
				<ScoreHistoryGraph runs={runs}/>
			</Card>
		);
	};

	// The runs as table rows: the run date in the label column, then grade and scores.
	const get_run_rows = ()=>{
		return runs.map((run)=>{
			return {
				id: run.id,
				label: format_run_date(run.page_data_date),
				grade: run.grade,
				scoring: run.scoring
			};
		});
	};

	const render_runs = ()=>{
		if(runs === null){
			return (
				<div className="sites-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		if(runs.length === 0){
			return (
				<p className="sites-empty">{c_NoRunsText}</p>
			);
		}

		return (
			<ScoreRowsTable rows={get_run_rows()} label_header="Date of Analysis" on_open={open_run}/>
		);
	};

	// The competitor rows as table rows: the site's name with its url under it, then grade and scores once the site is
	// fully processed; while it is still being crawled, analyzed or having its history imported, its progress instead,
	// as the site list shows it, whatever it has already scored, and the row cannot be opened.
	const get_competitor_table_rows = ()=>{
		return competitor_rows.map((row)=>{
			const pre_processing = is_site_pre_processing(row);
			return {
				id: row.id,
				label: row.title ? row.title : strip_url(row.url),
				sublabel: strip_url(row.url),
				grade: pre_processing ? null : row.grade,
				scoring: pre_processing ? null : row.scoring,
				status_text: pre_processing ? get_pre_processing_label(row) : row.status_text,
				openable: !is_site_dashboard_pending(row)
			};
		});
	};

	// The competitor table, last on a main site's dashboard once it has a competitor.
	const render_competitors = ()=>{
		if(!competitor_rows || (competitor_rows.length === 0)){
			return null;
		}

		return (
			<Card className="site-dashboard-graph-card site-dashboard-competitors-card">
				<h5 className="site-dashboard-section-title">Competitors</h5>
				<ScoreRowsTable rows={get_competitor_table_rows()} label_header="Site" on_open={open_competitor}/>
			</Card>
		);
	};

	const render_competitor_modal = ()=>{
		if(!show_competitor_modal){
			return null;
		}

		return (
			<SiteModal
				site={null}
				competitor_of_site={site}
				on_save={save_competitor}
				on_close={close_competitor_modal}/>
		);
	};

	// Shown instead of the dashboard while the site's history is still being imported or analyzed, as when the
	// dashboard's address is opened directly.
	const render_pre_processing = ()=>{
		return (
			<Container className="site-view site-dashboard-view">
				<p className="sites-empty">{get_dashboard_pending_text(site)}</p>
			</Container>
		);
	};

	if(site && is_site_dashboard_pending(site)){
		return render_pre_processing();
	}

	return (
		<Container className="site-view site-dashboard-view">
			{render_header()}
			{error_message && <Alert variant="danger" className="site-view-error">{error_message}</Alert>}
			{pdf_error_message && <Alert variant="danger" className="site-view-error">{pdf_error_message}</Alert>}
			{render_graph()}
			{render_runs()}
			{render_competitors()}
			{render_competitor_modal()}
			<PdfExportModal show={fetching_pdf} stage={pdf_stage}/>
		</Container>
	);
}

export default SiteDashboardView;
